import { useState } from "react";
import { X, UserPlus, Search, Plus } from "lucide-react";
import type { Company } from "../api/HiringContactsApi";
import { fetchCompanies } from "../api/HiringContactsApi";
import { useHiringContacts } from "../modal/HiringContactsContext";
import CreateCompanyModal from "./CreateCompanyModal";

interface Props {
  onClose: () => void;
}

export default function CreateContactModal({ onClose }: Props) {
  const { isAdmin, handleCreateContact } = useHiringContacts();
  const [fullName, setFullName] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  const [companyQuery, setCompanyQuery] = useState("");
  const [results, setResults] = useState<Company[]>([]);
  const [searching, setSearching] = useState(false);
  const [showCreateCompany, setShowCreateCompany] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = fullName.trim() && linkedinUrl.trim() && selectedCompany;

  async function searchCompanies(e: React.FormEvent) {
    e.preventDefault();
    if (!companyQuery.trim()) return;
    setSearching(true);
    try {
      const res = await fetchCompanies({ search: companyQuery.trim(), page_size: 10 }, isAdmin);
      setResults(res.items);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }

  async function confirm() {
    if (!canSubmit || !selectedCompany) return;
    setSaving(true);
    setError(null);
    try {
      await handleCreateContact(fullName.trim(), linkedinUrl.trim(), selectedCompany.id);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create contact. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-surface border border-border rounded-2xl shadow-2xl w-full max-w-md p-6">
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-primary/15 flex items-center justify-center">
              <UserPlus className="w-5 h-5 text-primary" />
            </div>
            <h2 className="text-base font-semibold text-foreground font-inter">New Contact</h2>
          </div>
          <button onClick={onClose} disabled={saving} className="text-muted-foreground hover:text-foreground transition-colors disabled:opacity-40">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-3 mb-5">
          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Full name</label>
            <input
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Jane Recruiter"
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">LinkedIn URL</label>
            <input
              type="text"
              value={linkedinUrl}
              onChange={(e) => setLinkedinUrl(e.target.value)}
              placeholder="https://www.linkedin.com/in/janerecruiter"
              className="w-full bg-surface-muted border border-border rounded-xl text-sm text-foreground font-inter px-3 py-2 focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-xs font-medium text-muted-foreground font-inter mb-1.5">Company</label>
            {selectedCompany ? (
              <div className="flex items-center justify-between bg-surface-muted/60 border border-border/60 rounded-xl px-3 py-2">
                <div>
                  <p className="text-sm text-foreground font-inter">{selectedCompany.name}</p>
                  <p className="text-xs text-muted-foreground font-inter">{selectedCompany.domain}</p>
                </div>
                <button
                  onClick={() => setSelectedCompany(null)}
                  className="text-xs text-primary hover:text-primary/80 font-inter"
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <form onSubmit={searchCompanies} className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground pointer-events-none" />
                    <input
                      type="text"
                      value={companyQuery}
                      onChange={(e) => setCompanyQuery(e.target.value)}
                      placeholder="Search companies…"
                      className="w-full bg-surface-muted border border-border rounded-xl pl-8 pr-3 py-2 text-sm text-foreground font-inter focus:outline-none focus:border-primary"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={searching}
                    className="px-3 py-2 text-sm text-foreground border border-border hover:border-border rounded-xl transition-colors font-inter"
                  >
                    Search
                  </button>
                </form>

                {results.length > 0 && (
                  <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                    {results.map((c) => (
                      <button
                        key={c.id}
                        onClick={() => { setSelectedCompany(c); setResults([]); }}
                        className="w-full text-left px-3 py-2 bg-surface-muted/60 hover:bg-surface-muted border border-border/60 rounded-lg text-sm text-foreground font-inter transition-colors"
                      >
                        {c.name} <span className="text-muted-foreground">· {c.domain}</span>
                      </button>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => setShowCreateCompany(true)}
                  className="mt-2 flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-inter"
                >
                  <Plus className="w-3.5 h-3.5" />
                  Create a new company
                </button>
              </>
            )}
          </div>
        </div>

        {error && (
          <p className="text-xs text-destructive font-inter mb-4 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
            {error}
          </p>
        )}

        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border hover:border-border rounded-xl transition-colors disabled:opacity-40 font-inter"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={saving || !canSubmit}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-inter"
          >
            <UserPlus className="w-4 h-4" />
            {saving ? "Creating…" : "Create Contact"}
          </button>
        </div>
      </div>

      {showCreateCompany && (
        <CreateCompanyModal
          onClose={() => setShowCreateCompany(false)}
          onCreated={(company) => { setSelectedCompany(company); setShowCreateCompany(false); }}
        />
      )}
    </div>
  );
}
